import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { HelcimPayModalService } from '../services/helcimpay-modal.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { TestPaymentService, VerifyResponse } from '../services/test-payment.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-payment-services-payments-verify-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Payments > Verify</h1>
          <p class="page-sub">Initialize checkout and submit card verification flow.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="card">
            <div class="card-hd">
              <span class="card-title">Verify</span>
            </div>

            <div class="form">
              <label class="field">
                <span>Organization</span>
                <select [(ngModel)]="organizationId" name="organizationId" (ngModelChange)="onOrganizationChange()" required>
                  <option [ngValue]="0">Select organization</option>
                  @for (organization of organizations; track organization.id) {
                    <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                  }
                </select>
              </label>
              <label class="field"><span>Amount</span><input type="number" [(ngModel)]="amount" name="amount" min="0" step="0.01" required /></label>
              <label class="field"><span>Currency</span><input type="text" [(ngModel)]="currency" name="currency" maxlength="3" required /></label>
              <label class="field"><span>IP Address</span><input type="text" [(ngModel)]="ipAddress" name="ipAddress" maxlength="45" required /></label>
              <label class="field"><span>Billing Name</span><input type="text" [(ngModel)]="billingName" name="billingName" maxlength="120" required /></label>
              <label class="field"><span>Billing Street</span><input type="text" [(ngModel)]="billingStreet1" name="billingStreet1" maxlength="120" required /></label>
              <label class="field"><span>Billing Postal Code</span><input type="text" [(ngModel)]="billingPostalCode" name="billingPostalCode" maxlength="20" required /></label>
              <label class="field"><span>Customer Code (optional)</span><input type="text" [(ngModel)]="customerCode" name="customerCode" maxlength="64" /></label>
              <label class="field"><span>Invoice Number (optional)</span><input type="text" [(ngModel)]="invoiceNumber" name="invoiceNumber" maxlength="64" /></label>
              <label class="field field--full"><span>Reference (optional)</span><input type="text" [(ngModel)]="reference" name="reference" maxlength="120" /></label>
              <div class="actions">
                <button type="button" [disabled]="isSubmitting || !helcimPayEnabled" (click)="submitVerify()">
                  {{ isSubmitting ? 'Submitting...' : 'Submit Verify' }}
                </button>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            @if (result) {
              <div class="result" [class.result--ok]="result.isVerified" [class.result--error]="!result.isVerified">
                <div><strong>Status:</strong> {{ result.status }}</div>
                <div><strong>Verified:</strong> {{ result.isVerified ? 'Yes' : 'No' }}</div>
                <div><strong>Card Token:</strong> {{ result.cardToken || 'N/A' }}</div>
                <div><strong>Approval Code:</strong> {{ result.approvalCode || 'N/A' }}</div>
                <div><strong>AVS Response:</strong> {{ result.avsResponse || 'N/A' }}</div>
                <div><strong>CVV Response:</strong> {{ result.cvvResponse || 'N/A' }}</div>
                <div><strong>Error:</strong> {{ result.errorMessage || 'N/A' }}</div>
                <div><strong>Processed (UTC):</strong> {{ result.processedAtUtc }}</div>
              </div>
            }
          </div>

          <div class="related-card">
            <h3>Quick Access</h3>
            <div class="related-grid">
              @for (link of relatedLinks; track link.route) {
                <a class="quick-link" [routerLink]="link.route">{{ link.label }}</a>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.related-card{margin-top:1rem;padding:1rem 1.1rem}.related-card h3{margin:0 0 .55rem}.related-grid{display:grid;gap:.6rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-link{border:1px solid var(--color-border);border-radius:.65rem;padding:.55rem .75rem;text-decoration:none;color:var(--color-text);background:color-mix(in srgb,var(--color-surface-1) 75%,transparent)}.quick-link:hover{border-color:color-mix(in srgb,var(--color-accent) 35%,var(--color-border))}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.field input,.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.actions button{border:1px solid var(--color-accent);background:var(--color-accent);color:#fff;border-radius:.5rem;padding:.45rem .8rem;cursor:pointer}.actions button:disabled{opacity:.7;cursor:not-allowed}.result{margin:0 1rem 1rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesPaymentsVerifyPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly helcimPayModalService = inject(HelcimPayModalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly helcimPayEnabled = environment.helcimPay.enabled;

  organizationId = 0;
  ipAddress = '192.168.1.1';
  amount = 0;
  currency = 'USD';
  billingName = '';
  billingStreet1 = '';
  billingPostalCode = '';
  customerCode = '';
  invoiceNumber = '';
  reference = '';
  shippingContactName = '';
  shippingContactEmail = '';
  organizations: OrganizationDto[] = [];
  isLoadingOrganizations = false;

  isSubmitting = false;
  errorMessage = '';
  result: VerifyResponse | null = null;

  readonly relatedLinks = [
    { label: 'Purchase', route: '/payment-services/payments/purchase' },
    { label: 'PreAuth', route: '/payment-services/payments/preauth' },
    { label: 'Capture', route: '/payment-services/payments/capture' },
    { label: 'Refund', route: '/payment-services/payments/refund' },
    { label: 'Reversal', route: '/payment-services/payments/reversal' }
  ];

  async ngOnInit(): Promise<void> {
    await this.loadOrganizationsAsync();
  }

  onOrganizationChange(): void {
    this.customerCode = this.organizationId > 0 ? String(this.organizationId) : '';
    const organization = this.organizations.find((item) => item.id === this.organizationId);
    this.shippingContactName = organization?.shippingContactName || '';
    this.shippingContactEmail = organization?.shippingContactEmail || '';
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }

      this.onOrganizationChange();
    } catch {
      this.organizations = [];
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async submitVerify(): Promise<void> {
    this.errorMessage = '';
    this.result = null;

    if (!this.helcimPayEnabled) {
      this.errorMessage = 'HelcimPay modal is disabled for this environment.';
      return;
    }

    if (
      this.organizationId <= 0 ||
      this.amount < 0 ||
      !this.currency.trim() ||
      !this.ipAddress.trim() ||
      !this.billingName.trim() ||
      !this.billingStreet1.trim() ||
      !this.billingPostalCode.trim()
    ) {
      this.errorMessage = 'Organization, amount (zero or greater), currency, IP address, and billing fields are required for Verify.';
      return;
    }

    this.isSubmitting = true;

    try {
      const initResponse = await this.testPaymentService.initializeCheckout({
        organizationId: this.organizationId,
        amount: this.amount,
        currency: this.currency.trim().toUpperCase(),
        paymentType: 'verify',
        reference: this.reference.trim() || undefined
      }).toPromise();

      if (!initResponse) {
        throw new Error('Failed to initialize Verify checkout session.');
      }

      const modalPayload = await this.helcimPayModalService.openModal(initResponse.checkoutToken);
      if (!modalPayload.cardToken?.trim()) {
        throw new Error('HelcimPay modal did not return a card token for Verify.');
      }

      const verifyResponse = await this.testPaymentService.verify({
        organizationId: this.organizationId,
        ipAddress: this.ipAddress.trim(),
        currency: this.currency.trim().toUpperCase(),
        amount: this.amount,
        cardToken: modalPayload.cardToken.trim(),
        billingName: this.billingName.trim(),
        billingStreet1: this.billingStreet1.trim(),
        billingPostalCode: this.billingPostalCode.trim(),
        customerCode: this.customerCode.trim() || undefined,
        invoiceNumber: this.invoiceNumber.trim() || undefined,
        reference: this.reference.trim() || undefined
      }).toPromise();

      if (!verifyResponse) {
        throw new Error('Verify request failed.');
      }

      this.result = verifyResponse;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Verify request failed.';
    } finally {
      this.isSubmitting = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
